import React, { useMemo } from 'react';

import { FormControl, FormHelperText, FormLabel, MenuItem, Select, Stack } from '@mui/material';

import type { PropertyRoomTypeWithImages } from '../../ImagesVideo/types';
import {
  getMissingRequiredPackageFields,
  getPackageRoomRateId,
  getPackageRoomTypeId,
  getRoomRateLabel,
  getSelectedRoomRateLabel,
} from '../utils';

const PACKAGE_STATUS_OPTIONS = ['content-approved', 'hidden', 'draft'] as const;

interface Props {
  offerPackage: App.Package;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  onChange: (nextPackageValues: Partial<App.Package> & Record<string, unknown>) => void;
}

export default function InventoryMappingTab({ offerPackage, propertyRoomTypes = [], onChange }: Props) {
  const selectedRoomTypeId = getPackageRoomTypeId(offerPackage) || '';
  const selectedRoomRateId = getPackageRoomRateId(offerPackage) || '';
  const selectedRoomType = useMemo(
    () => propertyRoomTypes.find((roomType) => roomType.id === selectedRoomTypeId),
    [propertyRoomTypes, selectedRoomTypeId],
  );
  const roomRates = selectedRoomType?.room_rates ?? [];
  const hasSelectedRoomTypeOption = propertyRoomTypes.some((roomType) => roomType.id === selectedRoomTypeId);
  const hasSelectedRoomRateOption = roomRates.some((roomRate) => roomRate.id === selectedRoomRateId);
  const selectedRoomRateLabel = useMemo(
    () => getSelectedRoomRateLabel({ offerPackage, roomRates, selectedRoomRateId }),
    [offerPackage, roomRates, selectedRoomRateId],
  );
  const missingRequiredFieldKeys = new Set(getMissingRequiredPackageFields(offerPackage).map((field) => field.key));
  const isRoomTypeMissing = missingRequiredFieldKeys.has('roomType');
  const isRatePlanMissing = missingRequiredFieldKeys.has('ratePlan');

  const handleRoomTypeChange = (roomTypeId: string) => {
    const nextRoomType = propertyRoomTypes.find((roomType) => roomType.id === roomTypeId);
    const nextRoomRates = nextRoomType?.room_rates ?? [];
    const hasCurrentRoomRate = nextRoomRates.some((roomRate) => roomRate.id === selectedRoomRateId);

    onChange({
      fk_room_type_id: roomTypeId || undefined,
      fk_room_rate_id: hasCurrentRoomRate ? selectedRoomRateId : undefined,
    });
  };

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
      <Stack spacing={1} sx={{ flex: 1 }}>
        <FormLabel>Room type</FormLabel>
        <FormControl size="small" fullWidth error={isRoomTypeMissing}>
          <Select value={selectedRoomTypeId} onChange={(event) => handleRoomTypeChange(event.target.value)}>
            {selectedRoomTypeId && !hasSelectedRoomTypeOption && (
              <MenuItem value={selectedRoomTypeId}>{selectedRoomTypeId}</MenuItem>
            )}
            {propertyRoomTypes.map((roomType) => (
              <MenuItem key={roomType.id} value={roomType.id}>
                {roomType.name || roomType.id}
              </MenuItem>
            ))}
          </Select>
          {isRoomTypeMissing && <FormHelperText>Room type is required</FormHelperText>}
        </FormControl>
      </Stack>

      <Stack spacing={1} sx={{ flex: 1 }}>
        <FormLabel>Rate plan</FormLabel>
        <FormControl
          size="small"
          fullWidth
          disabled={!selectedRoomTypeId || roomRates.length === 0}
          error={isRatePlanMissing}
        >
          <Select
            value={selectedRoomRateId}
            onChange={(event) => onChange({ fk_room_rate_id: event.target.value || undefined })}
          >
            {selectedRoomRateId && !hasSelectedRoomRateOption && (
              <MenuItem value={selectedRoomRateId}>{selectedRoomRateLabel}</MenuItem>
            )}
            {roomRates.map((roomRate) => (
              <MenuItem key={roomRate.id} value={roomRate.id}>
                {getRoomRateLabel(roomRate) || roomRate.id}
              </MenuItem>
            ))}
          </Select>
          {isRatePlanMissing && <FormHelperText>Rate plan is required</FormHelperText>}
        </FormControl>
      </Stack>

      <Stack spacing={1} sx={{ flex: 1 }}>
        <FormLabel>Status</FormLabel>
        <FormControl size="small" fullWidth>
          <Select value={offerPackage.status || ''} onChange={(event) => onChange({ status: event.target.value })}>
            {offerPackage.status &&
              !(PACKAGE_STATUS_OPTIONS as ReadonlyArray<string>).includes(offerPackage.status) && (
                <MenuItem value={offerPackage.status}>{offerPackage.status}</MenuItem>
              )}
            {PACKAGE_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
}
